import { useMutation, useQuery } from '@apollo/client';
import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import {
  OAuthProvider,
  OAuthSetting,
  ReadOAuthProvidersArg,
  readOAuthProvidersQuery,
  ReadOAuthProvidersResponse,
  UpdateOAuthProviderArg,
  updateOAuthProviderMutation,
  UpdateOAuthProviderResponse,
} from 'apis/partnerIntegrations';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

import {
  OAuthProviderConfigurationFormValues,
  OAuthProviderSettingsConfiguration,
} from '../../components/settingsConfiguration/oauthProviderSettingsConfiguration';
import { useOAuthAuthorization } from '../../hooks/useOAuthAuthorization';
import styles from '../../oauthProviderConfigurationModal.module.scss';
import type { OAuthProviderConfigurationModalResult } from '../../oauthProviderConfigurationModal.types';

type OAuthProviderUpdateModalProps = {
  isOpen: boolean;
  providerId: string;
  meta?: {
    resolve: (result: OAuthProviderConfigurationModalResult) => void;
  };
  hide: VoidFunction;
};

type ProviderFormValues = {
  provider: OAuthProvider;
};

/** A modal for updating OAuth providers */
export const OAuthProviderModalUpdate = ({
  isOpen,
  providerId,
  meta,
  hide,
}: OAuthProviderUpdateModalProps): JSX.Element => {
  const formId = uuid();
  const selectForm = useForm<ProviderFormValues>({
    defaultValues: { provider: undefined },
  });
  const selectedProvider = selectForm.watch('provider');

  const { connectionStatus, handleAuthorization, checkConnectionStatus } =
    useOAuthAuthorization(selectedProvider?.id);

  const notify = useNotifications();

  const defaultFormValues: OAuthProviderConfigurationFormValues = {};
  const form = useForm<OAuthProviderConfigurationFormValues>({
    defaultValues: defaultFormValues,
  });

  // Fetch the OAuth provider if supplied
  const {
    loading: isProviderLoading,
    error: providerError,
    data,
    refetch: refetchProvider,
  } = useQuery<ReadOAuthProvidersResponse, ReadOAuthProvidersArg>(
    readOAuthProvidersQuery,
    {
      skip: !providerId,
      variables: {
        id: providerId,
      },
    },
  );

  useEffect(() => {
    if (data) {
      const providerData = data.Management.oAuthProviders;
      if (providerData.length > 0) {
        selectForm.setValue('provider', providerData[0]);
      }
    }
  }, [data, selectForm]);

  const [updateProvider, { loading: isUpdatingProvider }] = useMutation<
    UpdateOAuthProviderResponse,
    UpdateOAuthProviderArg
  >(updateOAuthProviderMutation, {
    refetchQueries: [
      {
        query: readOAuthProvidersQuery,
        variables: { id: providerId },
      },
    ],
    onError: (err) => {
      notify(
        {
          title: 'Error while updating the OAuth provider.',
          text: err.cause?.toString() ?? err?.toString(),
        },
        NotificationType.Error,
      );
    },
    onCompleted: async (result) => {
      const provider = result.Management.updateOAuthProvider;

      await refetchProvider();

      notify(
        {
          title: 'Success',
          text: `"${provider.name}" OAuth provider has been updated.`,
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      resolveModalResponse(provider);
    },
  });

  const isLoading = isUpdatingProvider || isProviderLoading;

  const onSubmit = (formData: OAuthProviderConfigurationFormValues) => {
    const settings: OAuthSetting[] = Object.entries(formData)
      .filter(([key, _]) => key !== 'name')
      .map(([key, setting]) => ({
        key,
        value: setting.value,
        secret: setting.secret,
      }));

    updateProvider({
      variables: {
        id: selectedProvider?.id,
        name: formData.name.value as string,
        settings: settings.filter((setting) => (setting.value ? true : false)),
      },
    });
  };

  const resolveModalResponse = useCallback(
    (provider: OAuthProvider) => {
      meta?.resolve({
        isSuccessful: true,
        provider,
      });
    },
    [meta],
  );

  const renderContent = () => {
    return (
      <>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            {selectedProvider && selectedProvider.strategy && (
              <OAuthProviderSettingsConfiguration
                mode="update"
                strategyId={selectedProvider.strategy}
                initialValues={selectedProvider}
              />
            )}
          </>
        )}

        {providerError && (
          <FormField>
            <Text.Paragraph>
              Error loading OAuth provider. Please try again or contact support.
            </Text.Paragraph>
          </FormField>
        )}
      </>
    );
  };

  // Determine the footer buttons based on the current step
  const renderModalFooter = () => {
    return (
      <ModalFoot
        confirmOnClick={async () => {
          await handleAuthorization();
          await checkConnectionStatus();

          if (connectionStatus === 'CONNECTED') {
            notify(
              {
                title: 'Success',
                text: `"${selectedProvider.name}" OAuth provider has been authorized.`,
                icon: 'i-done',
                time: 5000,
              },
              NotificationType.Success,
            );
          }
        }}
        confirmSecondaryOnClick={form.handleSubmit(onSubmit)}
        cancelOnClick={hide}
        confirmName={
          connectionStatus === 'CONNECTED' ? 'Reauthorize' : 'Authorize'
        }
        confirmSecondaryName="Update Provider"
        cancelName="Cancel"
        confirmProps={{
          variant: 'primary',
          isLoading,
        }}
        confirmSecondaryProps={{
          type: 'submit',
          form: formId,
          isLoading,
          disabled: isLoading || !selectedProvider?.id,
        }}
        cancelProps={{
          variant: 'secondary',
        }}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={hide}
      className={styles.oauthProviderConfigurationModal}
    >
      <ModalHead
        title={<Text.Heading>Update OAuth provider</Text.Heading>}
        onClose={hide}
      />

      <ModalBody className={styles.oauthProviderConfigurationModalBody}>
        <FormProvider {...form}>
          <form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
            <FormGrid>{renderContent()}</FormGrid>
          </form>
        </FormProvider>
      </ModalBody>

      {renderModalFooter()}
    </Modal>
  );
};
